/**
 * FeasibilityProfileSection — radar chart (SF3).
 */
import React from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { ChartFrame } from '../../ui/ChartFrame';
import { reportChartColor } from '../../../lib/reportChartTheme';
import { EditableHeading } from './EditableHeading';
import type { SiteRagReport, RagCategory } from '../../../types/siteRag';
import type { ReportTheme } from '../../../types/reportTheme';
import { ContextualKeyFindingsBlock } from './ContextualKeyFindingsBlock';
import { EditorialSectionShell } from './EditorialSectionShell';
import {
  REPORT_SECTION_H2_CLASS,
  REPORT_SECTION_HEADING_TEXT_CLASS,
  reportSectionHeadingStyle,
} from '../../../lib/reportSectionHeading';
import { roleColor } from '../../../lib/reportRoleColors';
import { reportSectionGapClass } from '../../../lib/reportSpacingTokens';
import {
  CHART_FRAME_BOUNDS, CHART_PLOT_MARGIN, CHART_INK, RADAR_OUTER_RADIUS,
} from '../../../lib/reportChartLayoutTokens';
import { tierLabel, getDisplayVerdict } from '../../../lib/reportVerdict';
import { EditorialVerdictMark } from './editorial';

/** Compact axis label for the radar polygon vertices — keeps tick text
 *  short enough to fit at the chart edge without colliding. */
const SHORT_LABELS: Record<string, string> = {
  'Grid & Power Infrastructure': 'Grid',
  'Grid & Power': 'Grid',
  'Cooling & Ambient Conditions': 'Cooling',
  'Cooling & Water': 'Cooling',
  'Site & Land': 'Site',
  'Regulatory & Permitting': 'Regulatory',
  'Logistics & Access': 'Logistics',
  'Ecological Impact': 'Ecological',
  'Water Resources': 'Water',
  'Air Quality': 'Air',
  'Regulatory Complexity': 'Regulatory',
  'Natural Hazards': 'Hazards',
};

function shortLabel(label: string): string {
  return SHORT_LABELS[label] || label.split(' & ')[0] || label.split(' ')[0];
}

function defaultHeading(reportType: string | undefined): string {
  switch (reportType) {
    case 'environmental':       return 'Environmental Profile';
    case 'power-availability':  return 'Power Profile';
    case 'water-resource':      return 'Water Resource Profile';
    case 'interconnection':     return 'Interconnection Profile';
    default:                    return 'Feasibility Profile';
  }
}

/** Radial ring max — the polygon scale is 0…RING_MAX. */
export const RADAR_RING_MAX = 100;

/**
 * J6 — tier → radius midpoint. Every status maps to a STRICTLY POSITIVE
 * radius (min 16), so a category with a missing/0 numeric score still draws
 * at a real ring position rather than collapsing to the center.
 */
export function radarTierMidpoint(status: RagCategory['status']): number {
  if (status === 'green') return 84;
  if (status === 'amber') return 50;
  return 16;
}

/**
 * J6 INVARIANT — the radial value the radar polygon is drawn from for one
 * category. Returns the REAL numeric score when it is a finite positive
 * figure (clamped to the ring), otherwise the tier midpoint. The result is
 * positive for legacy measured categories. Explicitly missing metrics instead
 * return null and are omitted from the scored polygon, with an Unassessed legend.
 */
export function categoryIsUnassessed(c: RagCategory): boolean {
  return c.metrics.length > 0 && c.metrics.every(metric => metric.missing === true);
}

export function radarScoreFor(c: RagCategory): number | null {
  if (categoryIsUnassessed(c)) return null;
  const s = c.score;
  if (typeof s === 'number' && Number.isFinite(s) && s > 0) {
    return Math.min(RADAR_RING_MAX, Math.max(0, s));
  }
  return radarTierMidpoint(c.status);
}

interface Props {
  report: SiteRagReport;
  theme: ReportTheme;
  isEditing: boolean;
  headingOverrides: Record<string, string>;
  setHeading: (key: string, value: string) => void;
  isVisible: (sectionId: string) => boolean;
  sectionStyle: (sectionId: string) => React.CSSProperties;
  omitSectionSidebar?: boolean;
  shellPadding?: boolean;
  sectionPageColumnCount?: (sectionId: string) => 1 | 2;
  /**
   * DEFECT-A — the page-flow classification (island|flow|span) for a section,
   * threaded from SiteRagReportView. Stamped as `data-section-flow` AT RENDER
   * (before pagination) so the radar EXHIBIT + its findings-prose tail are
   * classified deterministically rather than relying on the post-layout
   * backfill (which runs AFTER pagedjs has already span-laid-out the section).
   * `undefined` for single-column / legacy presets ⇒ no stamp (byte-stable).
   */
  sectionFlowMode?: (sectionId: string) => string | undefined;
  sectionNumberProps?: { 'data-section-number'?: string };
  /** N1.5 — VITE_EDITORIAL_LAYOUTS on: render the editorial hero-numeral opener. */
  editorialActive?: boolean;
  /** N1.5 — 1-based TOC display number for the hero numeral (NaN ⇒ none). */
  editorialSectionNumber?: number;
}

export const FeasibilityProfileSection: React.FC<Props> = ({
  report, theme, isEditing, headingOverrides, setHeading, isVisible, sectionStyle,
  omitSectionSidebar = false,
  shellPadding = true,
  sectionPageColumnCount,
  sectionFlowMode,
  sectionNumberProps,
  editorialActive = false,
  editorialSectionNumber,
}) => {
  if (!isVisible('radarChart')) return null;

  const cats = report.categories;
  if (!cats || cats.length === 0) {
    return (
      <EditorialSectionShell
        id="radarChart"
        data-section="radarChart"
        data-section-id="radarChart"
        extraClassName="py-3 text-xs text-text-muted italic"
        style={sectionStyle('radarChart')}
        data-testid="feasibility-profile-empty"
        omitSectionSidebar={omitSectionSidebar}
        shellPadding={shellPadding}
        theme={theme}
        {...sectionNumberProps}
      >
        Scoring not applicable for this report type.
      </EditorialSectionShell>
    );
  }

  const hasRedMetricOrHighWaterStress = (c: RagCategory): boolean =>
    (c.metrics ?? []).some(
      (m) => m.status === 'red' || (m.id === 'water_stress' && /high|extreme/i.test(String(m.value ?? ''))),
    ) || /Water Stress Index:\s*(?:High|Extreme)\s*\(red\)/i.test(c.explanation ?? '');
  const hasMissingOrPlanningOnly = (c: RagCategory): boolean =>
    (c.metrics ?? []).some((m) => m.missing) ||
    (c.metrics ?? []).some((m) => /planning-default\s+elcc/i.test(`${m.label ?? ''} ${m.value ?? ''}`)) ||
    /evidence gap\s*[—–-]\s*excluded from scoring|planning-default\s+elcc/i.test(c.explanation ?? '');
  const effectiveCategoryStatus = (c: RagCategory): RagCategory['status'] =>
    c.status === 'green' && (hasRedMetricOrHighWaterStress(c) || hasMissingOrPlanningOnly(c))
      ? 'amber'
      : c.status;

  // Bind the polygon to the REAL per-category score (0–100), normalised to
  // the ring max (RADAR_RING_MAX). When a raw green category reflects only
  // climate or state-policy proxies while local supply, design, or zoning
  // readiness is Conditional (amber), cap its plotted readiness radius at the
  // Watch/Conditional ring midpoint (50) so proxy opportunity scores do not
  // visually plot as Strong readiness.
  const RING_MAX = RADAR_RING_MAX;
  const effectiveRadarScoreFor = (c: RagCategory): number | null => {
    const raw = radarScoreFor(c);
    if (raw == null) return null;
    if (c.status === 'green' && effectiveCategoryStatus(c) === 'amber') {
      return Math.min(raw, radarTierMidpoint('amber'));
    }
    return raw;
  };
  const radarData = cats.filter(c => !categoryIsUnassessed(c)).map(c => ({
    category: shortLabel(c.label),
    // `score` is the normalised radial value the polygon is drawn from.
    score: effectiveRadarScoreFor(c),
    fullMark: RING_MAX,
  }));

  // Concentric ring scale — sentiment-first qualitative bands (no raw
  // 0–100 numbers on the report doc, per the VS-pack invariant). Each ring
  // value maps to the band word the reader sees at that radius.
  const RING_TICKS = [25, 50, 75, RING_MAX];
  // One distinct band word per concentric ring (inner→outer), matching the
  // caption's "Risk · Watch · Fair · Strong" ladder. Keyed on the exact tick
  // value so the outer ring (RING_MAX) and the 75 ring no longer BOTH resolve
  // to 'Strong' — the prior `v >= 75` threshold duplicated 'Strong' and dropped
  // 'Risk' entirely (two Strong rings, no Risk ring).
  const RING_LABEL_BY_TICK: Record<number, string> = {
    25: 'Risk',
    50: 'Watch',
    75: 'Fair',
    [RING_MAX]: 'Strong',
  };
  const ringBand = (v: number): string => RING_LABEL_BY_TICK[v] ?? '';

  // Per-axis status legend — status dot + short label + qualitative band.
  // Sentiment-first: surfaces the tier word, never the numeric score.
  const statusColor = (status: RagCategory['status']): string =>
    status === 'green'
      ? roleColor(theme, 'positive')
      : status === 'amber'
        ? roleColor(theme, 'warning')
        : roleColor(theme, 'negative');
  const statusBand = (c: RagCategory): string => {
    if (c.status === 'green') {
      if (c.id === 'cooling' && hasRedMetricOrHighWaterStress(c)) {
        return 'Conditional (climate favorable; supply/design unverified)';
      }
      if (c.id === 'regulatory' && hasMissingOrPlanningOnly(c)) {
        return 'Conditional (state proxy; local zoning unverified)';
      }
      if (hasMissingOrPlanningOnly(c) || hasRedMetricOrHighWaterStress(c)) {
        return 'Conditional (partial basis)';
      }
    }
    return tierLabel(c.status, report.reportType, 'category');
  };
  // Per-axis status legend rows — status dot colour + short label + qualitative
  // band word. SENTIMENT-FIRST: the band word is the ONLY per-axis readout in
  // the Option-1 right-column legend; the raw 0–100 score is NEVER surfaced on
  // the report doc (a hard invariant in this file).
  const legendRows = cats.map(c => ({
    id: c.id,
    label: shortLabel(c.label),
    fullLabel: c.label,
    color: categoryIsUnassessed(c) ? roleColor(theme, 'muted') : statusColor(effectiveCategoryStatus(c)),
    band: categoryIsUnassessed(c) ? 'Unassessed' : statusBand(c),
  }));

  // DEFECT-A — resolve the section's page column count + flow classification
  // ONCE so the shell stamps `data-section-flow` AT RENDER (before pagination,
  // not via the post-layout backfill that runs too late). The actual overflow
  // fix is the `flowInColumn` prop on the findings block below: the
  // `.template-export-cols-2 [data-section-columns="1"]` print rule makes this
  // 1-col section a single-column MULTICOL, and the block's default
  // `column-span:all` then becomes a SPANNER that pagedjs cannot fragment —
  // freezing the whole 2019px section onto one page. Dropping that span lets
  // the section fragment like any other 1-col prose section (e.g. actionPlan),
  // so the exhibit + prose flow across the page seam.
  const radarCols: 1 | 2 = sectionPageColumnCount ? sectionPageColumnCount('radarChart') : 1;
  const radarFlow = sectionFlowMode?.('radarChart');

  // P2.6 — primary series colour now reads the REPORT theme palette via the
  // single chart-theme adapter instead of the app-level `var(--chart-1)`
  // (dark-theme cyan) token, so a preset / tenant palette reskins the radar.
  const radarColor = reportChartColor(theme, 0);

  // N1.5C — SHARED heading node across the two shell modes (TOC anchor / edit
  // wiring identical). Editorial mode: the shell owns the opener. Classic mode:
  // the caller renders the `<h2>` below (byte-identical to the retired path).
  const headingNode = (
    <EditableHeading
      headingKey="radarChart"
      defaultText={defaultHeading(report.reportType)}
      className={REPORT_SECTION_HEADING_TEXT_CLASS}
      isEditing={isEditing}
      overrides={headingOverrides}
      onChange={setHeading}
    />
  );

  return (
    <>
    {/* DEFECT-A — the radar EXHIBIT (heading + verdict + radar/legend snapshot)
         is its OWN short [data-section] that never needs to fragment, so the
         snapshot export-capture `<img>` always renders. pagedjs DROPS a
         replaced-element img (and its kept-with-next heading) whenever the
         [data-section] CONTAINING it has to fragment across pages — so the long
         findings prose is rendered as a SEPARATE section BELOW (it has no img to
         drop and fragments freely). */}
    <EditorialSectionShell
      id="radarChart"
      extraClassName={reportSectionGapClass(theme.density)}
      data-testid="feasibility-profile-section"
      data-section="radarChart"
      data-section-columns={sectionPageColumnCount ? String(radarCols) : undefined}
      data-section-flow={radarFlow}
      style={{ scrollMarginTop: '80px', ...sectionStyle('radarChart') }}
      omitSectionSidebar={omitSectionSidebar}
      shellPadding={shellPadding}
      theme={theme}
      {...sectionNumberProps}
      editorialActive={editorialActive}
      sectionNumber={editorialSectionNumber}
      heading={headingNode}
    >
      {/* Classic mode (flag OFF) — the caller-owned head. P2.4 canonical H2 helper:
           class + style byte-identical to the prior inline literals
           (REPORT_SECTION_H2_CLASS + a `mb-6` modifier; reportSectionHeadingStyle
           === { borderColor, color, fontFamily }). Editorial mode: the shell owns
           the opener, so this is suppressed. */}
      {!editorialActive && (
        <h2
          className={`${REPORT_SECTION_H2_CLASS} mb-6`}
          style={reportSectionHeadingStyle(theme)}
          data-heading="true"
        >
          {headingNode}
        </h2>
      )}

      {/* 18A editorial verdict mark — composed BENEATH the head, ABOVE the
           radar exhibit. DEFECT-A (Option-1): the prior per-axis tier DATASHEET
           that sat here is REMOVED — the Option-1 right-column legend (radar
           LEFT, legend RIGHT inside the exhibit below) now carries the per-axis
           bands, so the datasheet was a redundant second copy. The verdict mark
           STAYS (the single qualitative headline). The composite NUMBER is
           intentionally never user-facing (verdict tier replaces it); we do NOT
           fabricate axis numbers — the legend shows the band word only. */}
      {(() => {
        const verdict = getDisplayVerdict(report);
        const showVerdict = (verdict.tier !== 'none' || verdict.savedDecisionPending) && verdict.label && verdict.label !== '—';
        if (!showVerdict) return null;
        return (
          <div
            className="mb-6"
            data-testid="feasibility-profile-verdict-rail"
            data-feasibility-verdict-rail="true"
            style={{
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
              breakAfter: 'avoid',
              pageBreakAfter: 'avoid',
            }}
          >
            <div
              style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}
              data-testid="feasibility-profile-verdict"
            >
              <span
                style={{
                  fontFamily: 'var(--report-body-font, inherit)',
                  fontSize: 10,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: roleColor(theme, 'muted'),
                }}
              >
                Assessment
              </span>
              <EditorialVerdictMark theme={theme} verdict={verdict} />
            </div>
            {/* REG-SCORE #2 (William's decision, 2026-07-02) — the LLM's score
                 adjustment is KEPT but LABELLED: it is never folded into the
                 deterministic score/verdict, so where the verdict renders we
                 surface it as a small muted context line. Absent field ⇒ no
                 markup (byte-stable for legacy reports / parity fixtures). */}
            {report.aiScoreAdjustment && report.aiScoreAdjustment.delta !== 0 && (
              <div
                style={{
                  fontFamily: 'var(--report-body-font, inherit)',
                  fontSize: 10,
                  marginTop: 4,
                  color: roleColor(theme, 'muted'),
                }}
                data-testid="feasibility-profile-ai-adjustment"
              >
                AI assessment adjustment: {report.aiScoreAdjustment.delta > 0 ? '+' : '−'}
                {Math.abs(report.aiScoreAdjustment.delta)}
                {report.aiScoreAdjustment.rationale ? ` – ${report.aiScoreAdjustment.rationale}` : ''}
                {' (not applied to the deterministic assessment)'}
              </div>
            )}
          </div>
        );
      })()}

      {/* VIEWER↔PDF PARITY — `overflow-visible` + `pb-2` so the rasteriser
           never clips the bottom legend row when this capture box is snapshot
           at the fixed ~700px print column; the legend lives BELOW the chart
           in normal flow and was being cropped with the chart's reserved box.

           BUG #7 (collapse-to-center) — the wrapper width is now a DETERMINISTIC
           `min(100%, 480px)` (inline style) instead of the prior pure-percentage
           `w-full max-w-[480px]`. The radar is the only report chart with polar
           `cx/cy="50%"` geometry: recharts derives its polygon radius from
           `getMaxRadius = min(width, height)/2`, and `outerRadius="66%"` then
           multiplies that. When the chart's responsively-measured WIDTH momentarily
           resolves to 0 — the offscreen paginate source's first paint, or a re-measure
           after Paged.js moves the live SVG into a fixed-height `overflow:hidden`
           `.pagedjs_page` — maxRadius becomes 0 and EVERY axis point collapses onto the
           dead center (the on-screen symptom). The PDF (a settled server render at a
           real viewport) never hits the 0-width window, so it plots the real clustered
           values. Pinning the box to a concrete px width means the inner
           recharts-responsive-container measures a stable, non-zero width on the very
           first layout pass, so the polar radius can no longer evaluate to 0. The
           `min(…, 100%)` keeps it responsive on a narrow interactive column (no
           classic / flag-off regression). */}
      {/* DEFECT-A — keep the snapshot exhibit ATOMIC (the radar/legend/caption is
           one figure). This wrapper SURVIVES the export-capture `<img>`
           replacement (only the inner `[data-export-capture]` node is swapped). */}
      <div style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }} data-exhibit-keep="true">
      <div
        className="overflow-visible pb-2"
        // DEFECT-A — the exhibit stays ATOMIC (radar + legend + caption snapshot
        // as one figure); the SECTION around it fragments, so the findings-prose
        // tail flows to the next page while the exhibit is never split.
        // `data-flow="span"` classifies it as a full-width exhibit for the
        // editorial 2-col deck.
        style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
        data-testid="feasibility-profile-radar"
        data-export-capture=""
        data-export-id="feasibility-profile-radar"
        data-export-title="Feasibility Profile"
        data-flow="span"
        data-span-columns="true"
      >
        {/* DEFECT-A Option-1 layout — radar LEFT, ranked band legend RIGHT (the
             approved "radar left, ranked legend right" exhibit). The side-by-side
             flex collapses the exhibit from ~700px (radar STACKED above a 6-row
             legend) to ~320px (height ≈ max(radar, legend)), so the section's
             findings-prose tail has room to start on the same page and fragment
             onward. `flex-wrap` drops the legend below the radar on a narrow
             interactive column. */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          {/* radarcol — BUG #7: a DETERMINISTIC non-zero width (`min(56%,380px)`
               + `minWidth:240`) so the inner recharts-responsive-container
               measures a stable, non-zero width on the first layout pass and the
               polar radius can never evaluate to 0 (collapse-to-center). */}
          <div
            className="overflow-visible"
            style={{ flex: '0 0 auto', width: 'min(56%, 380px)', minWidth: 320 }}
          >
        {radarData.length > 0 ? <ChartFrame
          exportId="feasibility-profile-radar"
          traceName="report:feasibility-radar"
          minWidth={100}
          aspect={CHART_FRAME_BOUNDS.radar.aspect}
          minHeight={CHART_FRAME_BOUNDS.radar.minHeight}
          maxHeight={CHART_FRAME_BOUNDS.radar.maxHeight}
          legibilityFloor={CHART_FRAME_BOUNDS.radar.legibilityFloor}
        >
          {/* Explicit margin so the PolarAngleAxis vertex labels (incl. the
               BOTTOM 'Logistics'/'Cooling' labels) never clip at the SVG edge
               at the fixed print width — RadarChart has no default margin.
               K4 — geometry (aspect / margin / outerRadius / dot) is tokenized
               via reportChartLayoutTokens so the radar shares the deliberate
               proportions of the other report charts.
               BUG #7 — the PolarRadiusAxis below carries an EXPLICIT `domain`
               ([0, RING_MAX]) so the radial value→radius mapping is fully
               deterministic; the radius polygon can collapse to the center ONLY
               when the chart width measures 0 (the responsive-container window the
               deterministic wrapper width above closes), never from an auto-derived
               radius domain. */}
          <RadarChart cx="50%" cy="50%" outerRadius={RADAR_OUTER_RADIUS} data={radarData} margin={CHART_PLOT_MARGIN.radar} data-assessed-category-count={radarData.length}>
            {/* Grid + axis-label chrome reads the theme neutral ramp so the
                 polygon scaffold follows the active preset. */}
            {/* 'rule' (not 'hair', now transparent) so the radar's concentric
                rings stay visible — the reader needs them to gauge each axis.
                K4 — straight (polygon) spokes read as a crisp graticule rather
                than the soft default circles. */}
            <PolarGrid stroke={roleColor(theme, 'rule')} gridType="polygon" />
            <PolarAngleAxis
              dataKey="category"
              tick={{ fontSize: CHART_INK.axisTickFontSize, fill: roleColor(theme, 'muted'), fontWeight: 600 }}
            />
            {/* Radial scale now LABELLED — concentric rings carry the
                 qualitative band word (Risk / Watch / Fair / Strong) so the
                 reader can gauge how far each axis reaches without a raw
                 0–100 number. */}
            <PolarRadiusAxis
              angle={90}
              domain={[0, RING_MAX]}
              // recharts @types skew (cloud npm-ci is stricter than local): the
              // `ticks` prop types as readonly TickItem[] but accepts numbers at
              // runtime — cast to the prop's own type so it builds on both.
              ticks={RING_TICKS as unknown as React.ComponentProps<typeof PolarRadiusAxis>['ticks']}
              tick={{ fontSize: CHART_INK.axisTickFontSize, fill: roleColor(theme, 'muted') }}
              tickFormatter={(v: number) => ringBand(v)}
              axisLine={false}
            />
            <Radar isAnimationActive={false}
              name="Score"
              dataKey="score"
              stroke={radarColor}
              fill={radarColor}
              fillOpacity={0.22}
              strokeWidth={2}
              dot={{ r: 2.5, fill: radarColor, strokeWidth: 0 }}
              activeDot={{ r: 4 }}
            />
          </RadarChart>
            </ChartFrame> : <p className="text-xs text-text-muted">No assessed categories are available for this chart.</p>}
          </div>

          {/* legendcol — single-column band legend (sentiment-first): status
               dot + short label + qualitative BAND WORD (Strong / Fair / Watch
               / Risk). NEVER a raw 0–100 score (a hard invariant in this file).
               Reads top-to-bottom beside the radar in the Option-1 split; each
               "label … band" row has the full legend-column width. */}
          <ul
            className="grid grid-cols-1 gap-y-2.5 m-0 list-none p-0"
            style={{ flex: '1 1 240px', minWidth: 220 }}
            data-testid="feasibility-profile-legend"
          >
          {legendRows.map(row => (
            <li
              key={row.id}
              className="grid items-baseline gap-x-3 text-xs"
              style={{
                color: roleColor(theme, 'body'),
                gridTemplateColumns: '92px minmax(0, 1fr)',
              }}
              title={`${row.fullLabel} – ${row.band}`}
              data-testid={`feasibility-profile-legend-${row.id}`}
            >
              <span className="inline-flex items-center gap-2 shrink-0 whitespace-nowrap">
                <span
                  aria-hidden="true"
                  className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: row.color }}
                />
                <span className="font-semibold whitespace-nowrap">{row.label}</span>
              </span>
              <span
                className="tabular-nums leading-snug"
                style={{
                  color: roleColor(theme, 'muted'),
                  minWidth: 0,
                  textAlign: 'left',
                  overflowWrap: 'break-word',
                }}
              >
                {row.band}
              </span>
            </li>
          ))}
          </ul>
        </div>

        {/* Scoring-basis caption — names what the rings/polygon mean so the
             chart is self-explanatory on a printed page. Full width BELOW the
             radar/legend split. */}
        <p
          className="mt-3 text-[10px] leading-snug"
          style={{ color: roleColor(theme, 'muted') }}
          data-testid="feasibility-profile-basis"
        >
          Each axis is a weighted feasibility category; greater reach toward the
          outer ring (Strong) is more favourable. Ring bands (Risk · Watch ·
          Fair · Strong) and the per-axis status reflect the site's qualitative
          assessment, not a published numeric score.
        </p>
      </div>
      </div>

    </EditorialSectionShell>

    {/* DEFECT-A — the findings-prose tail as a SEPARATE [data-section]
         (radarChartFindings) so it FRAGMENTS across pages INDEPENDENTLY of the
         radar-exhibit section above (the exhibit img can no longer be dropped by
         a fragmenting parent). It carries radarChart's ORDER + visibility (via
         `sectionStyle('radarChart')`) so it sits immediately after the exhibit
         and hides in lockstep, and `data-section-flow` so the editorial 2-col
         deck columnises it. It is NOT in `sectionOrder`, so it adds NO TOC entry
         / section number — it is a continuation of the radar section. The bottom
         hairline (the section separator) lives here, at the true section end.
         `data-flow="flow"` + `flowInColumn` drop the block's own `column-span:all`
         freeze so it renders as a normal fragmentable block (its finding cards
         keep their per-card `break-inside:avoid`). */}
    <EditorialSectionShell
      id="radarChartFindings"
      extraClassName={reportSectionGapClass(theme.density)}
      data-section="radarChartFindings"
      data-section-columns={sectionPageColumnCount ? String(radarCols) : undefined}
      data-section-flow={radarFlow}
      style={sectionStyle('radarChart')}
      omitSectionSidebar={omitSectionSidebar}
      shellPadding={shellPadding}
      theme={theme}
    >
      <div data-flow="flow">
        <ContextualKeyFindingsBlock report={report} theme={theme} flowInColumn />
      </div>
    </EditorialSectionShell>
    </>
  );
};
